import React from "react";
import Footer from "../components/Footer";
import { TopBar } from "./Ticket";
import { ZoneMap, ZoneChecker, CITIES, RADIUS_KM } from "../components/espaces-verts/ZoneMap";
import { Leaf, Scissors, Sprout, Trees, Wind, Trash2, Flower2, Building2, Landmark, Factory, ArrowRight, CheckCircle2, MapPin } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/58b0a64d-2d51-4608-b7c2-5645fa033b5b/images/3f36cf57dc4db0ae0bb2c620b4071f09833b9d764eb2ec3919e6f2121b3c5cb6.jpeg";

const PRESTATIONS = [
  { icon: Scissors, title: "Tonte des pelouses", text: "Tonte régulière avec ramassage ou mulching, finitions au fil et bordures nettes." },
  { icon: Trees, title: "Taille de haies & arbustes", text: "Taille d'entretien et de formation, selon les périodes recommandées pour chaque essence." },
  { icon: Sprout, title: "Désherbage", text: "Désherbage manuel, thermique ou mécanique des allées, parkings et massifs — sans produits phytosanitaires." },
  { icon: Wind, title: "Débroussaillage", text: "Remise en état des zones enherbées, talus, friches et abords de bâtiments." },
  { icon: Leaf, title: "Ramassage de feuilles", text: "Passages d'automne pour garder accès, parkings et gouttières propres et sûrs." },
  { icon: Flower2, title: "Entretien de massifs", text: "Bêchage, paillage, plantations saisonnières et arrosage des massifs d'accueil." },
  { icon: Trash2, title: "Évacuation des déchets verts", text: "Enlèvement et valorisation en déchetterie ou plateforme de compostage agréée." },
];

const CIBLES = [
  { icon: Building2, title: "Entreprises & sièges", text: "Abords de bureaux, parkings, espaces d'accueil : une première impression soignée pour vos visiteurs." },
  { icon: Landmark, title: "Copropriétés & bailleurs", text: "Résidences, parties communes extérieures, contrats annuels avec calendrier de passages." },
  { icon: Factory, title: "Sites industriels & collectivités", text: "Zones d'activités, bâtiments publics, écoles : interventions planifiées hors horaires d'affluence." },
];

const ATOUTS = ["Un interlocuteur unique nettoyage + espaces verts", "Passages pointés sur site (GPS) et rapport photo", "Contrat annuel avec calendrier de passages", "Équipe formée, assurée, matériel professionnel", "Déchets verts évacués et valorisés", "Devis gratuit sous 24 h"];

export default function EspacesVerts() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <TopBar />
      <main className="flex-1" data-testid="espaces-verts-page">
        <section className="relative overflow-hidden bg-neutral-950 text-white">
          <img src={HERO} alt="Entretien des espaces verts d'une entreprise" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28 grid md:grid-cols-[1.2fr_1fr] gap-10 items-end">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase mb-4 flex items-center gap-2"><Leaf size={14} /> Espaces verts B2B</p>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight">Entretien des espaces verts <span className="text-emerald-400">dans un rayon de {RADIUS_KM} km</span> autour de Servins</h1>
              <p className="text-neutral-300 mt-6 text-base md:text-lg max-w-xl">Tonte, taille, désherbage, ramassage de feuilles et évacuation des déchets verts pour les entreprises, copropriétés et collectivités de l'Artois et du bassin minier — par la même équipe qui entretient vos locaux.</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <a href="/#devis" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-semibold px-5 py-3 rounded-md" data-testid="green-cta-hero">Demander un devis <ArrowRight size={16} /></a>
                <a href="#zone" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-semibold px-5 py-3 rounded-md" data-testid="green-cta-zone"><MapPin size={16} /> Voir la zone d'intervention</a>
              </div>
            </div>
            <ul className="grid grid-cols-1 gap-2 text-sm">
              {ATOUTS.map((a) => <li key={a} className="flex items-start gap-2 bg-white/10 backdrop-blur rounded-lg px-3 py-2"><CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {a}</li>)}
            </ul>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">Nos prestations</p>
          <h2 className="font-heading font-bold text-base md:text-lg text-neutral-900 max-w-2xl">Un entretien complet des extérieurs, planifié à l'année ou ponctuel</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="green-prestations">
            {PRESTATIONS.map((p) => (
              <div key={p.title} className="border border-neutral-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3"><p.icon size={18} /></span>
                <h3 className="font-semibold text-neutral-900">{p.title}</h3>
                <p className="text-sm text-neutral-600 mt-1.5">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-neutral-50 border-y border-neutral-200">
          <div className="max-w-6xl mx-auto px-5 py-16 md:py-20">
            <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">Pour qui ?</p>
            <h2 className="font-heading font-bold text-base md:text-lg text-neutral-900">Des extérieurs à la hauteur de votre image professionnelle</h2>
            <div className="grid md:grid-cols-3 gap-5 mt-8">
              {CIBLES.map((c) => (
                <div key={c.title} className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <span className="w-10 h-10 rounded-xl bg-neutral-900 text-white flex items-center justify-center mb-3"><c.icon size={18} /></span>
                  <h3 className="font-semibold text-neutral-900">{c.title}</h3>
                  <p className="text-sm text-neutral-600 mt-1.5">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="zone" className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">Zone d'intervention</p>
          <h2 className="font-heading font-bold text-base md:text-lg text-neutral-900">{RADIUS_KM} km autour de notre siège — 61 rue de la Mairie, 62530 Servins</h2>
          <p className="text-neutral-600 mt-3 max-w-2xl">Pour garantir réactivité et tarifs maîtrisés (déplacements, évacuation des déchets verts), nos prestations espaces verts sont proposées aux sites situés dans ce rayon : Artois, bassin minier, Béthunois, Douaisis et métropole lilloise sud.</p>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-8 items-start">
            <ZoneMap />
            <div className="space-y-5">
              <ZoneChecker />
              <div className="bg-white border border-neutral-200 rounded-2xl p-5">
                <p className="font-heading font-bold text-neutral-900">Principales villes desservies</p>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="green-cities">
                  {CITIES.map((c) => <span key={c} className="text-xs font-medium text-neutral-700 bg-neutral-100 px-2.5 py-1 rounded-md">{c}</span>)}
                </div>
                <p className="text-xs text-neutral-500 mt-3">… et toutes les communes situées à moins de {RADIUS_KM} km de Servins (Pas-de-Calais / Nord).</p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 pb-16 md:pb-24">
          <div className="bg-neutral-950 text-white rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div><p className="font-heading font-bold text-xl">Un devis espaces verts sous 24 h</p><p className="text-neutral-400 text-sm mt-1">Indiquez « Entretien des espaces verts » dans le formulaire : nous vous rappelons pour une visite gratuite du site.</p></div>
            <a href="/#devis" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-semibold px-5 py-3 rounded-md" data-testid="green-cta-bottom">Demander un devis <ArrowRight size={16} /></a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
